import { useState, useEffect, useRef, useMemo, } from 'react';
import { Radio } from 'antd';
import { useAtom } from 'jotai';
import ReactEcharts from 'echarts-for-react';
import { pointsPerSideState, calibrateState, scanningState } from '../stateManagement/processState';
import { colorsState } from '../stateManagement/graphState';
import { mapState } from '../stateManagement/commonState';
import { getIntensityScatterChartConfig } from './configs/intensityScatterChartConfig';

const IntensityScatterChart = ({ updateSpectrumLineChart }) => {
    const [pointsPerSide] = useAtom(pointsPerSideState);
    const [colors] = useAtom(colorsState);
    const [map] = useAtom(mapState);
    const [calibrate] = useAtom(calibrateState);
    const [dataType, setDataType] = useState('Max');
    const intensityChartRef = useRef(null);
    const mapRef = useRef(null);
    const [scanning] = useAtom(scanningState);

    const initialOption = useMemo(() => {
        const opt = getIntensityScatterChartConfig(colors, []);
        return opt;
    }, []);

    const intensityChartEventHandlerRef = useRef({
        click: e => {
            if (mapRef.current[e.dataIndex]) {
                updateSpectrumLineChart({
                    index: e.dataIndex,
                    rawSpectrum: mapRef.current[e.dataIndex].rawSpectrum,
                    badPixelCorrectedSpectrum: mapRef.current[e.dataIndex].badPixelCorrectedSpectrum,
                    smoothedSpectrum: mapRef.current[e.dataIndex].smoothedSpectrum,
                    baselineCorrectedSpectrum: mapRef.current[e.dataIndex].baselineCorrectedSpectrum,
                    intensityCorrectedSpectrum: mapRef.current[e.dataIndex].intensityCorrectedSpectrum,
                    normalizedSpectrum: mapRef.current[e.dataIndex].normalizedSpectrum,
                    interpolatedSpectrum: mapRef.current[e.dataIndex].interpolatedSpectrum,
                    processedSpectrum: mapRef.current[e.dataIndex].processedSpectrum,
                    integrationTime: mapRef.current[e.dataIndex].integrationTime,
                    laserPower: mapRef.current[e.dataIndex].power,
                });
            }
        },
    });

    const [intensityScatterChartOption, setIntensityScatterChartOption] = useState(getIntensityScatterChartConfig(colors, map));

    useEffect(() => { mapRef.current = map; }, [map]);

    useEffect(() => {
        setIntensityScatterChartOption(prevOption => {
            let newOption = { ...prevOption };
            newOption.series[0].data = map.length ?
                map.map((e, i) => [i, dataType === 'Max' ? Math.max(...e.rawSpectrum) : e.rawSpectrum.reduce((a, b) => a + b, 0), e.label, e.x, e.y, e.z, e.mlOutput, e.power, e.integrationTime, e.reconstructionError]) : [];
            newOption.series[1].data = map.length ?
                map.map((e, i) => [i, dataType === 'Max' ? Math.max(...e.processedSpectrum) : e.processedSpectrum.reduce((a, b) => a + b, 0), e.label, e.x, e.y, e.z, e.mlOutput, e.power, e.integrationTime, e.reconstructionError]) : [];
            newOption.series[2].data = map.length ?
                map.map((e, i) => [i, e.signalToNoise]) : [];
            newOption.series[3].data = map.length ?
                map.map((e, i) => [i, e.autocorrelationWidth]) : [];
            newOption.series[4].data = map.length ?
                map.map((e, i) => [i, e.lowFrequencyEnergy]) : [];
            // newOption.visualMap.seriesIndex = [1];
            // newOption.visualMap.categories = [...new Set(map.map(e => e.label))];
            // newOption.visualMap.inRange.color = calibrate ? ['#434343', ...colors] : colors;
            newOption.xAxis.min = 0;
            newOption.yAxis.min = 0;
            let estimatedNumPoints = pointsPerSide * pointsPerSide;
            if (calibrate) { estimatedNumPoints += 11; }
            if (map.length === 0) {
                newOption.xAxis.max = estimatedNumPoints;
                newOption.yAxis.max = 65535;
            } else {
                newOption.xAxis.max = scanning ? (map.length < estimatedNumPoints ? estimatedNumPoints : map.length) : map.length;
                newOption.yAxis.max = null;
            }
            return newOption
        });
    }, [map, pointsPerSide, colors, dataType, calibrate, scanning]);

    useEffect(() => {

        if (intensityChartRef.current) {
            intensityChartRef.current.getEchartsInstance().setOption(intensityScatterChartOption, false);
        }

    }, [intensityScatterChartOption]);

    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',  // Left text | Chart | Right text
            alignItems: 'center',
            color: '#8c8c8c',
            fontSize: '12px',
        }}>
            {/* <Radio.Group
                size='small'
                style={{ justifySelf: "end", marginRight: '14px' }}
                options={[{ label: 'Max', value: 'Max' }, { label: 'Sum', value: 'Sum' }]}
                value={dataType}
                optionType="button"
                onChange={e => { setDataType(e.target.value) }}
            /> */}
            <p style={{ margin: '-90px 0 0 0', writingMode: 'sideways-lr', textOrientation: 'mixed' }}>Maximum Intensity</p>
            <ReactEcharts
                ref={intensityChartRef}
                option={initialOption}
                onEvents={intensityChartEventHandlerRef.current}
                style={{ width: "100%" }}
            />
            <p style={{ margin: '-90px 0 0 0', writingMode: 'vertical-rl', textOrientation: 'mixed' }}>dB/channels/%</p>
        </div>
    );
};

export default IntensityScatterChart;